// A branch history's page (docs/design.md "Branch histories"): the store
// the history view and the range views read, a `BranchHost`
// (state/rangeHost.ts) with no conversation, made for a ref already
// resolved (state/ref/fetch.ts `resolveRef`; views/RefView.svelte resolves
// the URL's locator before it makes one), so a store always has its
// subject. It reads the ref's tip, the history from that tip a window at a
// time (`loadOlder` reads the next older one), makes its track
// (logic/refTrack.ts), and keeps beside them what the page shows of it:
// the branch's force-pushes from GitHub's activity and what each replaced,
// the open pull requests from the branch, the head's checks and their
// annotations, `.gitattributes` at the tip, the files the window changes
// (read when the Files view asks), and (on demand) each commit's pull
// requests and checks. Marks and notes are the repository's, as on a
// compare's page.
//
// Live updates, as a compare's: window focus (at most once a minute), a
// reconnect or a resync reads the tip again; a new tip reads the history
// from it again, as many windows as were held. Notes and marks notices of
// the repository reload those; a checks notice for the head reads its
// checks. Nothing polls. A branch deleted while its page is open leaves
// what was read shown, saying so (`gone`).
import { contentAt, restAll } from '../api/gh';
import { installUrl } from '../api/errors';
import { failureOf, type Failure } from '../api/failure';
import { onIdentityChange } from '../api/identity';
import type { CheckContext, PrFile } from '../api/queries';
import { LiveEvents, pausedForRateLimit, shouldRefetch, type Notice, type Trigger } from '../api/sse';
import type { LoadView } from '../loading/steps';
import { oneStep } from '../loading/steps';
import { generatedMatcher } from '../logic/categorize';
import { contextFails } from '../logic/checks';
import { refTrack, type ForcePush, type ReplacedRun } from '../logic/refTrack';
import { emptyTrack, type Track } from '../logic/track';
import { subjectLabel, type RefSubject } from '../subject';
import type { HeadChecks } from './compare.svelte';
import { pullsFrom, type CompareCommit, type HeadPull } from './compare/fetch';
import { MarksStore, type MarksNotice } from './marks.svelte';
import { acquireNotes, type NotesStore } from './notes.svelte';
import { commitRollup, type CheckAnnotation } from './pr/fetch';
import { PrPrefs } from './prefs.svelte';
import type { BranchHost, FilesSoFar } from './rangeHost';
import {
  fetchCommitFacts,
  fetchForcePushes,
  fetchWindow,
  MAX_REF_PAGES,
  refTip,
  RefNotFound,
  replacedRun,
  windowFiles,
  type CommitFacts,
  type HistoryWindow,
  type RefKind,
  type ResolvedRef,
  type WindowFiles,
} from './ref/fetch';
import { session } from './session.svelte';

/** The most check runs at the head whose annotations are read (failing first). */
const MAX_ANNOTATED_RUNS = 10;

/** What a history's store reads of GitHub: the page's own reads by default; tests give fakes. */
export interface RefWorld {
  tip(subject: RefSubject, kind: RefKind, signal: AbortSignal): Promise<string>;
  window(subject: RefSubject, tip: string, page: number, signal: AbortSignal): Promise<HistoryWindow>;
  pushes(subject: RefSubject, signal: AbortSignal): Promise<ForcePush[]>;
  replaced(subject: RefSubject, tip: string, before: string, signal: AbortSignal): Promise<ReplacedRun>;
  files(subject: RefSubject, base: string, head: string, signal: AbortSignal): Promise<WindowFiles>;
  pulls(subject: RefSubject, signal: AbortSignal): Promise<HeadPull[]>;
  checks(subject: RefSubject, head: string, signal: AbortSignal): Promise<HeadChecks>;
  annotations(subject: RefSubject, run: CheckContext, signal: AbortSignal): Promise<CheckAnnotation[]>;
  gitattributes(subject: RefSubject, at: string, signal: AbortSignal): Promise<string | null>;
  facts(subject: RefSubject, shas: readonly string[], signal: AbortSignal): Promise<Map<string, CommitFacts>>;
  /** Listens for notices and triggers (LiveEvents); returns how to stop. */
  listen(topics: string[], handler: (topic: string, notice: Notice | null) => void): () => void;
}

const visible = (signal: AbortSignal) => ({ priority: 'visible' as const, signal });
const background = (signal: AbortSignal) => ({ priority: 'background' as const, signal });

export const GITHUB_WORLD: RefWorld = {
  tip: (subject, kind, signal) => refTip(subject, kind, visible(signal)),
  window: (subject, tip, page, signal) => fetchWindow(subject, tip, page, visible(signal)),
  pushes: (subject, signal) => fetchForcePushes(subject.owner, subject.repo, subject.ref, background(signal)),
  replaced: (subject, tip, before, signal) => replacedRun(subject.owner, subject.repo, tip, before, background(signal)),
  files: (subject, base, head, signal) => windowFiles(subject, base, head, visible(signal)),
  pulls: (subject, signal) => pullsFrom(subject.owner, subject.repo, subject.owner, subject.ref, background(signal)),
  checks: async (subject, head, signal) => {
    const got = await commitRollup(subject.owner, subject.repo, head, background(signal));
    return { contexts: got?.contexts?.nodes ?? [], truncated: !!got?.contexts?.pageInfo?.hasNextPage };
  },
  annotations: async (subject, run, signal) =>
    (await restAll<Omit<CheckAnnotation, 'check'>>(`repos/${subject.owner}/${subject.repo}/check-runs/${run.databaseId}/annotations?per_page=100`, background(signal))).map((a) => ({ ...a, check: run.name ?? 'check' })),
  gitattributes: (subject, at, signal) => contentAt(subject.owner, subject.repo, '.gitattributes', at, background(signal)),
  facts: (subject, shas, signal) => fetchCommitFacts(subject.owner, subject.repo, shas, background(signal)),
  listen: (topics, handler) => {
    const live = new LiveEvents(topics, handler);
    return () => live.close();
  },
};

/** The history as read: the tip it was read from, its commits newest first, how many windows they are, and whether older ones exist. */
export interface RefHistory {
  tip: string;
  commits: readonly CompareCommit[];
  pages: number;
  more: boolean;
}

/** The page of a resolved ref: see the file comment. */
export class RefStore implements BranchHost {
  readonly subject: RefSubject;
  /** What the ref is: a branch (which has force-pushes and pull requests from it), a tag, or a commit. */
  readonly kind: RefKind;
  /** The repository's default branch, when GitHub said. */
  readonly defaultBranch: string | null;
  readonly conversation = null;
  /** Where the page is: reading the history, showing it, the ref is gone (`notFound` says why), or the read failed (`failure`). */
  status: 'loading' | 'ready' | 'notfound' | 'error' = $state('loading');
  failure: Failure | null = $state(null);
  notFound: string | null = $state(null);
  /** GitHub no longer has the ref shown (a branch deleted since it was read): why, while what was read stays shown. */
  gone: string | null = $state(null);
  history: RefHistory | null = $state.raw(null);
  /** The branch's force-pushes, newest first, as far back as GitHub keeps its activity; null until read (or when the read failed: `pushesError`). */
  pushes: readonly ForcePush[] | null = $state.raw(null);
  pushesError: string | null = $state(null);
  /** What each force-push replaced, by its `before` commit. */
  runs: ReadonlyMap<string, ReplacedRun> = $state.raw(new Map());
  readonly track: Track = $derived.by(() => (this.history ? refTrack({ commits: this.history.commits, hasOlder: this.history.more, filtered: this.subject.path !== null, pushes: this.pushes ?? [], runs: this.runs }) : emptyTrack()));
  /** A window older than those held is being read. */
  loadingOlder = $state(false);
  /** Why the last older window could not be read; null while it could. */
  olderError: string | null = $state(null);
  /** The open pull requests from the branch; null until read (a tag's or a commit's: none asked). */
  pulls: HeadPull[] | null = $state.raw(null);
  checks: HeadChecks | null = $state.raw(null);
  annotations: Readonly<Record<string, CheckAnnotation[]>> = $state.raw({});
  generated: (path: string) => boolean = $state.raw(() => false);
  /** Each commit's pull requests and checks, once asked for (`loadFacts`); a commit not asked about is absent. */
  facts: ReadonlyMap<string, CommitFacts> = $state.raw(new Map());
  factsError: string | null = $state(null);
  readonly prefs: PrPrefs;
  readonly marks: MarksStore;
  readonly notes: NotesStore;
  unrecorded = $state(false);
  /** A commit's history never moves; a branch's or a tag's may. */
  readonly closed: boolean;
  readonly filesSoFar: FilesSoFar | null = null;
  diffHead: string | null = $state(null);
  /** The files the window changes base → head, for the window they were read for (`files` is null for another). */
  private listed: { key: string; files: WindowFiles } | null = $state.raw(null);
  private filesError: { key: string; failure: Failure } | null = $state.raw(null);
  private listing: { key: string; run: Promise<void> } | null = null;
  private diffOwner: object | null = null;
  private loading: Promise<void> | null = null;
  private older: Promise<void> | null = null;
  private lastLoadAt = 0;
  private abort = new AbortController();
  private asked = new Set<string>();
  private factsWanted = false;
  private stop: () => void = () => {};
  private releaseNotes: () => void;
  private unwatch: () => void;
  private disposed = false;

  constructor(
    resolved: ResolvedRef,
    private world: RefWorld = GITHUB_WORLD,
  ) {
    this.subject = resolved.subject;
    this.kind = resolved.kind;
    this.defaultBranch = resolved.defaultBranch;
    this.closed = resolved.kind === 'commit';
    this.prefs = new PrPrefs(this.subject);
    this.marks = new MarksStore(this.subject.owner, this.subject.repo, null, () => (this.unrecorded = true));
    ({ store: this.notes, release: this.releaseNotes } = acquireNotes(this.subject.owner, this.subject.repo, () => (this.unrecorded = true)));
    this.unwatch = onIdentityChange(() => this.dispose());
  }

  /** The ref is the repository's default branch. */
  get isDefault(): boolean {
    return this.kind === 'branch' && this.subject.ref === this.defaultBranch;
  }
  /** Older windows than those held can be read (more exist, and fewer than MAX_REF_PAGES are held). */
  get canLoadOlder(): boolean {
    return !!this.history?.more && this.history.pages < MAX_REF_PAGES;
  }

  /** The window the files are of: its base and head. */
  private get filesKey(): string {
    return `${this.track.base.sha}|${this.track.head}`;
  }
  get files(): readonly PrFile[] | null {
    return this.listed?.key === this.filesKey ? this.listed.files.files : null;
  }
  get filesHead(): string | null {
    return this.files ? this.track.head : null;
  }
  get filesFailure(): Failure | null {
    if (this.status === 'error') return this.failure;
    return this.filesError?.key === this.filesKey ? this.filesError.failure : null;
  }
  get totals(): { added: number; removed: number } {
    return this.listed?.key === this.filesKey ? this.listed.files.totals : { added: 0, removed: 0 };
  }
  /** Files past GitHub's 300 that could not be listed: null when a tree was too large to read, else 0. */
  get filesMissing(): number | null {
    return this.listed?.key === this.filesKey ? this.listed.files.missing : 0;
  }

  get filesLoadView(): LoadView {
    return this.loadView();
  }
  loadView(): LoadView {
    const label = `Reading the history of ${this.subject.ref}${this.subject.path === null ? '' : ' in ' + this.subject.path}`;
    return oneStep(subjectLabel(this.subject), label, this.status === 'loading' ? 'loading' : this.status === 'error' && this.failure ? { failure: this.failure, what: 'this history' } : 'ready');
  }

  get serverDataText(): string | null {
    if (this.unrecorded) return `Changeling can't reach ${this.subject.owner}/${this.subject.repo} through an installation it knows about, so it keeps no notes or review marks here. If you just installed it, reload.`;
    return null;
  }
  get readOnly(): boolean {
    return this.unrecorded;
  }
  get readOnlyText(): string | null {
    return this.unrecorded ? this.serverDataText : null;
  }
  readonly installLink: string | null = $derived(installUrl(session.config?.app_slug, session.config?.github_web_url));

  /**
   * Read the ref's tip, and the history from it when it moved (joining a
   * read under way): as many windows as are held, from the newest. The
   * first read shows the load screen; a later one replaces what is shown
   * when it lands, and leaves it in place when it fails or the ref is gone.
   */
  load(): Promise<void> {
    if (this.loading) return this.loading;
    const first = !this.history;
    if (first) this.status = 'loading';
    const signal = this.abort.signal;
    const run = (async () => {
      try {
        const tip = await this.world.tip(this.subject, this.kind, signal);
        if (this.disposed) return;
        this.lastLoadAt = Date.now();
        if (this.history?.tip !== tip) {
          const want = Math.max(1, this.history?.pages ?? 1);
          const commits: CompareCommit[] = [];
          let pages = 0;
          let more = true;
          while (pages < want && more) {
            const w = await this.world.window(this.subject, tip, pages + 1, signal);
            if (this.disposed) return;
            commits.push(...w.commits);
            pages++;
            more = w.more;
          }
          this.history = { tip, commits, pages, more };
          this.afterTip(tip, signal);
        }
        this.status = 'ready';
        this.failure = null;
        this.gone = null;
      } catch (e) {
        if (this.disposed || signal.aborted) return;
        if (e instanceof RefNotFound && !first) this.gone = e.message;
        else if (e instanceof RefNotFound) {
          this.notFound = e.message;
          this.status = 'notfound';
        } else if (first) {
          this.failure = failureOf(e);
          this.status = 'error';
        }
      } finally {
        this.loading = null;
      }
    })();
    this.loading = run;
    return run;
  }

  /**
   * Read the next older window (the timeline's "older" lead-in, the
   * Commits view's Load older), from the tip the held ones were read from,
   * and put its commits before them: the track's base moves back, the
   * reader's range (by SHA) stays.
   */
  loadOlder(): Promise<void> {
    if (this.older) return this.older;
    const h = this.history;
    if (!h || !this.canLoadOlder) return Promise.resolve();
    this.loadingOlder = true;
    this.olderError = null;
    const run = (async () => {
      try {
        const w = await this.world.window(this.subject, h.tip, h.pages + 1, this.abort.signal);
        // A new tip read meanwhile holds its own windows.
        if (this.disposed || this.history !== h) return;
        this.history = { ...h, commits: [...h.commits, ...w.commits], pages: h.pages + 1, more: w.more };
        if (this.factsWanted) void this.loadFacts();
      } catch (e) {
        if (!this.disposed) this.olderError = `Older commits could not be read: ${failureOf(e).message}`;
      } finally {
        this.older = null;
        this.loadingOlder = false;
      }
    })();
    this.older = run;
    return run;
  }

  open(): Promise<void> {
    const repo = `${this.subject.owner}/${this.subject.repo}`.toLowerCase();
    const topics = [`repo:${repo}`];
    if (session.user) topics.push(`user:${session.user.id}`);
    this.stop = this.world.listen(topics, (topic, notice) => this.onNotice(topic, notice));
    void this.marks.load();
    return this.load();
  }

  onNotice(topic: string, notice: Notice | null): void {
    if (this.disposed) return;
    if (!notice) {
      const trigger = topic as Trigger;
      if (trigger === 'open' || !this.history || pausedForRateLimit() || !shouldRefetch(trigger, this.lastLoadAt)) return;
      void this.load();
      void this.marks.load();
      void this.notes.reload();
      return;
    }
    const ours = typeof notice.repo === 'string' && notice.repo.toLowerCase() === `${this.subject.owner}/${this.subject.repo}`.toLowerCase();
    if (notice.kind === 'notes' && ours) void this.notes.heard(notice);
    else if ((notice.kind === 'marks' || notice.kind === 'flags') && ours) void this.marks.onNotice(notice as MarksNotice);
    else if (notice.kind === 'checks') {
      if (this.track.head && (!notice.head_sha || notice.head_sha === this.track.head)) void this.readChecks(this.track.head, this.abort.signal);
      // A held commit's checks read while running are read again: the one named, or (naming none) every one.
      if (this.factsWanted) void this.loadFacts(notice.head_sha ? [notice.head_sha] : 'pending');
    }
  }

  /** What is read of a new tip: the head's checks, `.gitattributes`, the force-pushes and what they replaced, the pull requests from the branch, and the facts asked for before. */
  private afterTip(tip: string, signal: AbortSignal) {
    const head = this.track.head;
    if (head) void this.readChecks(head, signal);
    void this.world.gitattributes(this.subject, tip, signal).then(
      (text) => {
        if (!this.disposed && this.history?.tip === tip) this.generated = generatedMatcher(text);
      },
      () => {},
    );
    if (this.kind === 'branch') {
      void this.readPushes(tip, signal);
      if (this.pulls === null) void this.world.pulls(this.subject, signal).then((p) => !this.disposed && (this.pulls = p), () => {});
    }
    // A commit's facts are read once: a new tip asks for the commits it brings, and again for those whose checks were running.
    if (this.factsWanted) void this.loadFacts('pending');
  }

  private async readPushes(tip: string, signal: AbortSignal) {
    let pushes: ForcePush[];
    try {
      pushes = await this.world.pushes(this.subject, signal);
    } catch (e) {
      if (!this.disposed && this.history?.tip === tip) this.pushesError = `This branch's force-pushes could not be read from GitHub: ${failureOf(e).message}`;
      return;
    }
    // What each push replaced never changes: kept by its before commit, asked only for a push not read before.
    // One GitHub could not answer shows its before commit alone (and is asked again on the next tip).
    const runs = new Map<string, ReplacedRun>([...this.runs].filter(([before]) => pushes.some((p) => p.before === before)));
    await Promise.all(
      pushes.filter((p) => !runs.has(p.before)).map((p) =>
        this.world.replaced(this.subject, tip, p.before, signal).then(
          (r) => void runs.set(p.before, r),
          () => {},
        ),
      ),
    );
    if (this.disposed || this.history?.tip !== tip) return;
    this.pushes = pushes;
    this.runs = runs;
    this.pushesError = null;
  }

  private async readChecks(head: string, signal: AbortSignal) {
    let checks: HeadChecks;
    try {
      checks = await this.world.checks(this.subject, head, signal);
    } catch {
      return;
    }
    if (this.disposed || this.track.head !== head) return;
    this.checks = checks;
    const annotated = checks.contexts
      .filter((c) => c.__typename === 'CheckRun' && c.status === 'COMPLETED' && typeof c.databaseId === 'number')
      .sort((a, b) => Number(contextFails(b)) - Number(contextFails(a)))
      .slice(0, MAX_ANNOTATED_RUNS);
    const got = await Promise.all(annotated.map((r) => this.world.annotations(this.subject, r, signal).catch(() => [] as CheckAnnotation[])));
    if (this.disposed || this.track.head !== head) return;
    const byPath: Record<string, CheckAnnotation[]> = {};
    for (const a of got.flat()) (byPath[a.path] ??= []).push(a);
    this.annotations = byPath;
  }

  /**
   * Read the pull requests and checks of every main-lane commit not asked
   * about yet (the Commits view shows them), newest first; from then on,
   * of the commits each window read and each new tip brings. Each commit's
   * are read once for the page's life, but for checks that were running
   * when read: `again` asks those again too (a new tip: every such one;
   * a checks notice: the commits it names, when their checks were running).
   */
  async loadFacts(again: 'pending' | readonly string[] | null = null): Promise<void> {
    this.factsWanted = true;
    const h = this.history;
    if (!h) return;
    const running = (s: string) => this.facts.get(s)?.checks === 'pending' && (again === 'pending' || (Array.isArray(again) && again.includes(s)));
    const shas = [...this.track.main]
      .reverse()
      .map((p) => p.sha)
      .filter((s) => running(s) || (!this.asked.has(s) && !this.facts.has(s)));
    if (!shas.length) return;
    for (const s of shas) this.asked.add(s);
    try {
      const got = await this.world.facts(this.subject, shas, this.abort.signal);
      if (this.disposed) return;
      this.facts = new Map([...this.facts, ...got]);
      this.factsError = null;
    } catch (e) {
      if (this.disposed) return;
      for (const s of shas) this.asked.delete(s);
      this.factsError = `The pull requests and checks of these commits could not be read: ${failureOf(e).message}`;
    }
  }

  // DiffSource: the files the window changes, read for the window held now.
  loadFiles(): Promise<void> {
    if (!this.history) return this.load().then(() => (this.history ? this.loadFiles() : undefined));
    const key = this.filesKey;
    if (this.listed?.key === key || !this.track.head) return Promise.resolve();
    if (this.listing?.key === key) return this.listing.run;
    const [base, head] = [this.track.base.sha, this.track.head];
    const run = (async () => {
      try {
        const files = await this.world.files(this.subject, base, head, this.abort.signal);
        if (!this.disposed) this.listed = { key, files };
      } catch (e) {
        if (!this.disposed) this.filesError = { key, failure: failureOf(e) };
      } finally {
        if (this.listing?.key === key) this.listing = null;
      }
    })();
    this.listing = { key, run };
    return run;
  }
  filesGrew(): Promise<void> {
    return this.loadFiles();
  }
  showDiff(owner: object, head: string | null): void {
    this.diffOwner = owner;
    this.diffHead = head;
  }
  hideDiff(owner: object): void {
    if (this.diffOwner !== owner) return;
    this.diffOwner = null;
    this.diffHead = null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abort.abort();
    this.stop();
    this.unwatch();
    this.marks.dispose();
    this.releaseNotes();
  }
}
